import axios from 'axios';
import Config from 'react-native-config';
import { api } from '../../services/api';
import { useGlobal } from '../../context/global';

export type ThreeDSProvider = 'stripe' | 'getnet' | null;

export function getCvcLength(brand?: string): number {
  return brand === 'Amex' ? 4 : 3;
}

export default function use3DS() {

  const { prodStripe } = useGlobal();

  const createStripeToken = ({
    cardNumber,
    expMonth,
    expYear,
    cvc,
  }: {
    cardNumber: string;
    expMonth: number;
    expYear: number;
    cvc: string;
  }) =>
    axios.post(
      'https://api.stripe.com/v1/tokens',
      `card[number]=${cardNumber}&card[exp_month]=${expMonth}&card[exp_year]=${expYear}&card[cvc]=${cvc}`,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${(Config.RN_ENVIRONMENT === 'production' || prodStripe) ? Config.RN_STRIPE_PUBLIC_KEY : Config.RN_STRIPE_PUBLIC_KEY_TEST}`,
        },
      },
    );

  const checkCardIs3ds = (card_id: string, tipo_operacao?: string, client_id?: string | null) =>
    api.get('/ehCartao3ds', {
      params: {
        card_id,
        tipo_operacao,
        client_id,
      },
    });

  const checkIs3ds = () =>
    api.get('/ehCompra3ds');

  const solicitaValida3dsGetnet = (card_id: string) =>
    api.get('/solicita_valida_3ds_getnet', {
      params: {
        card_id,
      },
    });

  const verificaStatus3dsGetnet = (ds_trans_id: string) =>
    api.get('/status_3ds_getnet', {
      params: {
        ds_trans_id,
      },
    });

  const verificaAutentica3dsGetnet = ({
    compra_id,
    token_challenge,
    access_token,
    three_ds_token,
    value,
  }: {
    compra_id: string;
    token_challenge: string;
    access_token: string;
    three_ds_token: string;
    value: number;
  }) =>
    api.get('/verifica_autentica_3ds_getnet', {
      params: {
        compra_id,
        token_challenge,
        access_token,
        three_ds_token,
        value,
      },
    });

  const solicitaCardBin = (card_id: string) =>
    api.get('/consulta_cards_getnet', {
      params: {
        card_id,
      },
    });

  const generateTokenGetnet = ({
    version,
    totalAmount,
    consumerAuthenticationInformation,
    items,
    additionalData,
    additionalObject,
  }: {
    version: string;
    totalAmount: string;
    consumerAuthenticationInformation: {
      overridePaymentMethod: string;
    };
    items?: Array<{
      name?: string;
      description?: string;
      sku?: string;
      quantity?: string;
      unitPrice?: string;
      totalAmount?: string;
    }>;
    additionalData?: string;
    additionalObject?: Record<string, any>;
  }) =>
    api.post('/generateToken', {
      version,
      totalAmount,
      consumerAuthenticationInformation,
      items,
      additionalData,
      additionalObject,
    });

  const authenticationsGetnet = ({
    token,
    orderInformation,
    paymentInformation,
    personalIdentification,
    consumerAuthenticationInformation,
    deviceInformation,
    additionalData,
    additionalObject,
  }: {
    token: string;
    orderInformation: {
      amountDetails: {
        currency: string;
        totalAmount: string;
      };
      shipTo?: {
        firstName?: string;
        lastName?: string;
        locality?: string;
        administrativeArea?: string;
        country?: string;
        address1?: string;
        address2?: string;
        postalCode?: string;
        method?: string;
        destinationCode?: string;
      };
      billTo: {
        firstName?: string;
        lastName?: string;
        email?: string;
        locality?: string;
        administrativeArea?: string;
        country?: string;
        address1?: string;
        address2?: string;
        postalCode?: string;
        homePhone?: string;
        mobilePhone?: string;
      };
      items?: Array<{
        name?: string;
        description?: string;
        sku?: string;
        quantity?: string;
        unitPrice?: string;
        totalAmount?: string;
      }>;
    };
    paymentInformation: {
      card: {
        number: string;
        expirationMonth: string;
        expirationYear: string;
        holderName: string;
        type: string;
      };
    };
    personalIdentification?: {
      id: string;
      type: string;
    };
    consumerAuthenticationInformation: {
      installmentTotalCount?: string;
      overridePaymentMethod: string;
    };
    deviceInformation?: {
      httpBrowserColorDepth?: string;
      httpBrowserJavaEnabled?: string;
      httpBrowserJavaScriptEnabled?: string;
      httpBrowserLanguage?: string;
      httpBrowserScreenHeight?: string;
      httpBrowserScreenWidth?: string;
      httpBrowserTimeDifference?: string;
      userAgentBrowserValue?: string;
    };
    additionalData?: string;
    additionalObject?: Record<string, any>;
  }) =>
    api.post('/authentications', {
      token,
      orderInformation,
      paymentInformation,
      personalIdentification,
      consumerAuthenticationInformation,
      deviceInformation,
      additionalData,
      additionalObject,
    });

  const authenticationResultsGetnet = ({
    token,
    tokenChallenge,
    orderInformation,
    consumerAuthenticationInformation,
    paymentInformation,
    additionalData,
    additionalObject,
  }: {
    token: string;
    tokenChallenge: string;
    orderInformation: {
      amountDetails: {
        currency: string;
        totalAmount: string;
      };
    };
    consumerAuthenticationInformation: {
      overridePaymentMethod: string;
    };
    paymentInformation: {
      card: {
        number: string;
        expirationMonth: string;
        expirationYear: string;
        holderName: string;
        type: string;
      };
    };
    additionalData?: string;
    additionalObject?: Record<string, any>;
  }) =>
    api.post('/authentication-results', {
      token,
      tokenChallenge,
      orderInformation,
      consumerAuthenticationInformation,
      paymentInformation,
      additionalData,
      additionalObject,
    });

  return {
    createStripeToken,
    checkCardIs3ds,
    checkIs3ds,
    solicitaCardBin, // consulta_cards_getnet
    generateTokenGetnet, // generateToken
    authenticationsGetnet, // authentications
    authenticationResultsGetnet, // authentication-results
    verificaAutentica3dsGetnet, // endpoint legado (mantido para compatibilidade)
    solicitaValida3dsGetnet, // api/v1/solicita_valida_3ds_getnet
    verificaStatus3dsGetnet,
  };
}
